// src/pages/public/ReviewPage.js
// Route: /review/:reviewId/:token  (public — no auth required)
// Client leaves a star rating + written review via unique token link.

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { trackEvent } from '../../utils/analytics';

const FUNCTIONS_BASE = 'https://us-west1-divine-frameworks-6cb16.cloudfunctions.net';

const toTitleCase = (str) => {
  if (!str) return str;
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

// ─── Wrapper (MODULE SCOPE — must NOT be inside ReviewPage or focus is lost on every keystroke) ──
const Wrapper = ({ children }) => (
  <div style={{
    minHeight: '100vh',
    background: '#FAFAFA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: 'Montserrat, system-ui, sans-serif',
  }}>
    <div style={{
      background: '#ffffff',
      borderRadius: '20px',
      boxShadow: '0 20px 60px -10px rgba(0,0,0,0.08)',
      padding: '48px 40px',
      maxWidth: '520px',
      width: '100%',
      textAlign: 'center',
    }}>
      {/* Logo / brand */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          width: '48px', height: '48px',
          background: '#1F2937',
          borderRadius: '12px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '12px',
        }}>
          <Star size={24} fill="white" color="white" />
        </div>
        <p style={{ fontSize: '13px', color: '#9CA3AF', margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: '600' }}>
          Divine Frameworks
        </p>
      </div>
      {children}
    </div>
  </div>
);

// ─── Star Rating Input (MODULE SCOPE) ─────────────────────────────────────────
// Uses <span role="button"> instead of <button> to avoid browser tooltip/hover box bug.
const StarRating = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  const labels = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];
  const GOLD = '#D4AF5A';
  const EMPTY = '#D1D5DB';

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '8px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            role="button"
            tabIndex={0}
            onClick={() => onChange(star)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(star); } }}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            aria-label={`${star} star${star !== 1 ? 's' : ''}`}
            style={{
              cursor: 'pointer',
              padding: '4px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.1s ease',
              transform: (hovered || value) >= star ? 'scale(1.15)' : 'scale(1)',
              userSelect: 'none',
            }}
          >
            <Star
              size={40}
              fill={(hovered || value) >= star ? GOLD : 'none'}
              color={(hovered || value) >= star ? GOLD : EMPTY}
              strokeWidth={1.5}
            />
          </span>
        ))}
      </div>
      {(hovered || value) > 0 && (
        <p style={{ textAlign: 'center', fontSize: '14px', color: '#D4AF5A', fontWeight: '600', marginBottom: '0' }}>
          {labels[hovered || value]}
        </p>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ReviewPage() {
  const { reviewId, token } = useParams();

  // page states: loading | valid | already_submitted | expired | error | submitted
  const [pageState, setPageState] = useState('loading');
  const [reviewData, setReviewData] = useState(null);

  // form state
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState('');

  // Validate the token on mount
  useEffect(() => {
    async function validate() {
      try {
        const res = await fetch(`${FUNCTIONS_BASE}/getReviewData?reviewId=${reviewId}&token=${token}`);
        const data = await res.json();

        if (!res.ok) {
          if (data.code === 'already_submitted') {
            setPageState('already_submitted');
          } else if (data.code === 'expired') {
            setPageState('expired');
          } else {
            setPageState('error');
          }
          return;
        }

        setReviewData(data);
        setDisplayName(toTitleCase(data.clientFirstName) || '');
        setPageState('valid');
      } catch (err) {
        console.error('ReviewPage validate error:', err);
        setPageState('error');
      }
    }
    validate();
  }, [reviewId, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');

    if (rating === 0) {
      setFieldError('Please select a star rating.');
      return;
    }
    if (!body.trim() || body.trim().length < 10) {
      setFieldError('Please write at least a sentence about your experience.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${FUNCTIONS_BASE}/submitReview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewId,
          token,
          rating,
          body: body.trim(),
          displayName: isAnonymous ? '' : displayName.trim(),
          isAnonymous,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setFieldError(data.error || 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }

      setPageState('submitted');
      trackEvent('review_submitted', { rating });
    } catch (err) {
      console.error('submitReview error:', err);
      setFieldError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  // ── Loading state ───────────────────────────────────────────────────────────
  if (pageState === 'loading') {
    return (
      <Wrapper>
        <div style={{ color: '#6B7280', fontSize: '15px' }}>Verifying your review link…</div>
      </Wrapper>
    );
  }

  // ── Already submitted ───────────────────────────────────────────────────────
  if (pageState === 'already_submitted') {
    return (
      <Wrapper>
        <CheckCircle size={48} color="#6B7280" style={{ margin: '0 auto 16px' }} />
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Review Already Submitted</h1>
        <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: '1.6' }}>You've already left a review for this session. Thank you!</p>
      </Wrapper>
    );
  }

  // ── Expired ─────────────────────────────────────────────────────────────────
  if (pageState === 'expired') {
    return (
      <Wrapper>
        <Clock size={48} color="#9CA3AF" style={{ margin: '0 auto 16px' }} />
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Review Link Expired</h1>
        <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: '1.6' }}>This review link has expired. Review links are valid for 30 days after your session.</p>
      </Wrapper>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────────────
  if (pageState === 'error') {
    return (
      <Wrapper>
        <AlertCircle size={48} color="#EF4444" style={{ margin: '0 auto 16px' }} />
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Invalid Review Link</h1>
        <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: '1.6' }}>This review link is invalid or has expired. Please contact your practitioner if you believe this is an error.</p>
      </Wrapper>
    );
  }

  // ── Success ─────────────────────────────────────────────────────────────────
  if (pageState === 'submitted') {
    return (
      <Wrapper>
        <CheckCircle size={56} color="#10B981" style={{ margin: '0 auto 16px' }} />
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>Thank You!</h1>
        <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: '1.7' }}>
          Your review has been submitted and will be published shortly.
          {reviewData?.practitionerName && ` Thank you for sharing your experience with ${toTitleCase(reviewData.practitionerName)}.`}
        </p>
        {rating > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '20px' }}>
            {[1,2,3,4,5].map(s => (
              <Star key={s} size={20} fill={s <= rating ? '#D4AF5A' : 'none'} color={s <= rating ? '#D4AF5A' : '#D1D5DB'} />
            ))}
          </div>
        )}
      </Wrapper>
    );
  }

  // ── Review Form ─────────────────────────────────────────────────────────────
  return (
    <Wrapper>
      <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '6px', lineHeight: '1.3' }}>
        How was your session?
      </h1>
      {reviewData && (
        <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '32px' }}>
          {reviewData.serviceName && <><strong style={{ color: '#374151' }}>{reviewData.serviceName}</strong> · </>}
          with <strong style={{ color: '#374151' }}>{toTitleCase(reviewData.practitionerName)}</strong>
        </p>
      )}

      <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
        {/* Star Rating */}
        <div style={{ marginBottom: '28px', textAlign: 'center' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Your Rating
          </label>
          <StarRating value={rating} onChange={setRating} />
        </div>

        {/* Written Review */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
            Your Experience <span style={{ color: '#9CA3AF' }}>*</span>
          </label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={5}
            placeholder="Share what you experienced, how you felt, and what made this session valuable…"
            style={{
              width: '100%',
              padding: '12px 14px',
              fontSize: '15px',
              border: '1.5px solid #E5E7EB',
              borderRadius: '10px',
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              color: '#111827',
              resize: 'vertical',
              lineHeight: '1.6',
            }}
            onFocus={e => { e.target.style.borderColor = '#6B7280'; }}
            onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
          />
          <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px', marginBottom: '0' }}>
            {body.length} characters · minimum 10
          </p>
        </div>

        {/* Display Name */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
            Your Name (optional)
          </label>
          <input
            type="text"
            value={isAnonymous ? '' : displayName}
            onChange={e => setDisplayName(e.target.value)}
            disabled={isAnonymous}
            placeholder={isAnonymous ? 'Anonymous' : 'How should we display your name?'}
            style={{
              width: '100%',
              padding: '10px 14px',
              fontSize: '15px',
              border: '1.5px solid #E5E7EB',
              borderRadius: '8px',
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              color: '#111827',
              background: isAnonymous ? '#F9FAFB' : '#ffffff',
            }}
            onFocus={e => { if (!isAnonymous) e.target.style.borderColor = '#6B7280'; }}
            onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
          />
        </div>

        {/* Anonymous toggle */}
        <div style={{ marginBottom: '28px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#6B7280' }}>
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={e => setIsAnonymous(e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: '#1F2937', cursor: 'pointer' }}
            />
            Post anonymously
          </label>
        </div>

        {fieldError && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '14px',
            color: '#DC2626',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            {fieldError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '14px',
            background: submitting
              ? '#374151'
              : 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
            backgroundSize: '400% 400%',
            animation: submitting ? 'none' : 'auroraShift 16s ease infinite',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: submitting ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.01em',
            transition: 'filter 0.2s ease, background 0.2s ease',
          }}
          onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.filter = 'brightness(1.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
        >
          {submitting ? 'Submitting…' : 'Submit Review'}
        </button>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#9CA3AF', marginTop: '16px', lineHeight: '1.5' }}>
          Reviews are moderated and typically published within 24 hours.
        </p>
      </form>
    </Wrapper>
  );
}
